import { format, subDays } from "date-fns";
import { UseRecordsOptions } from "../../api/useRecords";
import { RecordContextType } from "../../components/record/Record";
import { GridCellParams } from "@mui/x-data-grid";
import { FIRST_EEG_SERVICE } from "../../utils/constants";
import { INSTRUMENTS } from "../psy-evaluations/components/scores-table/scoresTableConstants";

// For text area fields
const charactersLimit = 1000;

export const evaluationsOptions = {
	fieldsToRemove: {
		fieldTypesToRemove: ["alias"]
	},
	groupsOptions: {
		fieldsWithExtraOptions: [
			{
				field: "files",
				fileFolder: "a09a0214-e0ee-46f2-a0b4-0c863836ba9e"
			},
			{
				field: "eo_images",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},
			{
				field: "ec_images",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},
			{
				field: "other_images",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},
			{
				field: "zscore_raw_data",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},
			{
				field: "eeg_report",
				fileFolder: "a09a0214-e0ee-46f2-a0b4-0c863836ba9e"
			},
			{
				field: "eeg_screen_images",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},
			{
				field: "instruments_photos",
				fileFolder: "93a1c6db-4b79-401f-9977-5b94351238e1"
			},

			{
				field: "evaluation_sent_by",
				sort: "user_id.first_name",
				enabled: true
			},
			{
				field: "evaluation_sent_by",
				sort: "user_id.first_name",
				enabled: true
			},
			{
				field: "eeg_session",
				createFilter: (params: any) => sessionFilter(params, "eeg"),
				enabled: true,
				relatedWith: "treatment",
				sort: "start_date"
			},
			{
				field: "medication",
				charactersLimit
			},
			{
				field: "evaluation_motivation",
				charactersLimit,
				hidden: {
					detailFn: (item: any) => item?.eeg_session?.service?.id !== FIRST_EEG_SERVICE,
					updateFn: (item: any) => item?.eeg_session?.service?.id !== FIRST_EEG_SERVICE
				}
			},
			{
				field: "medical_psychological_history",
				charactersLimit,
				hidden: {
					detailFn: (item: any) => item?.eeg_session?.service?.id !== FIRST_EEG_SERVICE,
					updateFn: (item: any) => item?.eeg_session?.service?.id !== FIRST_EEG_SERVICE
				}
			},
			{
				field: "aditional_information",
				charactersLimit
			},
			{
				field: "ec_absolute_incidence",
				charactersLimit
			},
			{
				field: "ec_conectivity",
				charactersLimit
			},
			{
				field: "eo_absolute_incidence",
				charactersLimit
			},
			{
				field: "eo_conectivity",
				charactersLimit
			},
			{
				field: "observations",
				charactersLimit
			},
			{
				field: "therapeutic_goals",
				hidden: {
					detailFn: (item: any) => item?.eeg_session?.service?.id === FIRST_EEG_SERVICE,
					updateFn: (item: any) => item?.eeg_session?.service?.id === FIRST_EEG_SERVICE
				}
			},
			{
				field: "device",
				enabled: true,
				settings: { required: true }
			},
			{
				field: "brain_maps_technician",
				sort: "user_id.first_name",
				enabled: true
			},
			{
				field: "report_session",
				createFilter: (params: any) => sessionFilter(params, "report"),
				enabled: true,
				relatedWith: "treatment",
				sort: "start_date",
				fieldsQuery: "id,technician.user_id.*,start_date,service.name,status",
				displayTemplate:
					"{{start_date}} - {{service.name}} - {{technician.user_id.first_name}} {{technician.user_id.last_name}} [{{status}}]"
			},
			{
				field: "treatment",
				disabled: true
			},
			{
				field: "instrument_responses",
				relatedTable: "instruments_responses",
				fieldsQuery: "id,status,date_created,sent_at,instrument_id.name",
				displayTemplate: "{{instrument_id.name}} ({{date_label}})",
				manipulateResult: (items: any) =>
					items.map((item: any) => ({
						...item,
						date_label: item.sent_at || item.date_created ? format(new Date(item.sent_at || item.date_created), "dd/MM/yyyy") : ""
					})),
				enabled: true,
				relatedWith: "treatment",
				createFilter: (treatment: any) => {
					if (!treatment?.id) {
						return {
							filter: { id: { _null: true } },
							fieldsQuery: "id,status,date_created,sent_at,instrument_id.name"
						};
					}
					const currentEvaluationId = getCurrentEvaluationId();
					const notClaimedByOtherEvaluation = currentEvaluationId
						? {
								_or: [
									{ evaluations: { _none: { id: { _nnull: true } } } },
									{ evaluations: { evaluations_id: { _eq: currentEvaluationId } } }
								]
							}
						: { evaluations: { _none: { id: { _nnull: true } } } };
					return {
						_and: [
							{ treatment: { _eq: treatment.id } },
							{ status: { _eq: "completed" } },
							{ instrument_id: { _nin: [INSTRUMENTS.INTAKE_A, INSTRUMENTS.INTAKE_C] } },
							notClaimedByOtherEvaluation
						]
					};
				},
				sort: "completed_at"
			},
			{
				field: "report_checked_by",
				sort: "user_id.first_name",
				enabled: true
			},
			{
				field: "cbcl_images",
				fileFolder: "cd95951a-1e6e-4ed6-a6df-eecacb508329"
			},
			{
				field: "d2_images",
				fileFolder: "cd95951a-1e6e-4ed6-a6df-eecacb508329"
			},
			{
				field: "bdi_images",
				fileFolder: "cd95951a-1e6e-4ed6-a6df-eecacb508329"
			},
			{
				field: "bsi_images",
				fileFolder: "cd95951a-1e6e-4ed6-a6df-eecacb508329"
			},
			{
				field: "asrs_images",
				fileFolder: "cd95951a-1e6e-4ed6-a6df-eecacb508329"
			},
			{
				field: "content_evaluation",
				charactersLimit: 200,
				style: {
					height: "580px",
					noParagraphSpacing: true,
					toolbarItems: [
						"heading",
						"|",
						"bold",
						"italic",
						"bulletedList",
						"numberedList",
						"|",
						"undo",
						"redo"
					]
				}
			}
		],
		isFormValid: (data: any) =>
			data.device ? { valid: true } : { valid: false, message: "Selecione um dispositivo" }
	},
	dataGridOptions: {
		fieldsToSearch: [
			{
				field: "treatment",
				displayTemplate: "{{treatment.client.client_id}}"
			}
		],
		extraColumns: {
			end: [
				{
					field: "other_form_done",
					headerName: "Formulário preenchido",
					width: 75,
					renderCell: (params: GridCellParams) => {
						const row = params.row;
						const otherFormDone = row?.treatment?.other_form_done;
						if (!otherFormDone) return null;
						return otherFormDone.toUpperCase();
					}
				}
			]
		}
	},
	createRelatedParams: (params: RecordContextType | undefined) => createEvaluationParams(params)
} as UseRecordsOptions;

export const createEvaluationParams = async (recordContext: RecordContextType | undefined) => {
	if (!recordContext?.item) return;
	if (recordContext.entity === "clients") return `[clients][id]=${recordContext.item.id}`;
	if (recordContext.entity === "leads") return `[lead][id]=${recordContext.item.id}`;
	if (recordContext.entity === "treatments")
		return `[clients][id]=${recordContext.item.client.id}&[treatments][id][*,client.*,client.user_id.*]=${recordContext.item.id}`;
};

export const sessionFilter = (params: any, type: "eeg" | "report") => {
	var filter: any = {};

	const eeg = [35, 39];
	const report = [37, 40];
	const treatmentId = params?.id;

	if (treatmentId) {
		filter = {
			_and: [
				{
					treatment: {
						id: {
							_eq: treatmentId
						}
					}
				},
				{
					service: {
						id: {
							_in: type === "eeg" ? eeg : report
						}
					}
				}
			]
		};
	}

	if (filter._and && type === "eeg") {
		filter._and.push({
			status: {
				_eq: "done"
			}
		});
	}
	if (filter._and && type === "report") {
		filter._and.push({
			start_date: {
				_gte: format(subDays(new Date(), 20), "yyyy-MM-dd")
			}
		});
		filter._and.push({
			status: {
				_nin: ["cancelled", "staff_unavailable", "missed"]
			}
		});
	}

	return filter;
};

const getCurrentEvaluationId = () => {
	const match = window.location.pathname.match(/\/evaluations\/(\d+)/);
	return match ? Number(match[1]) : null;
};
